'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { KeyboardShortcutsModal, type ShortcutContext } from './keyboard-shortcuts-modal';

/**
 * Determină contextul scurtăturilor pe baza pathname-ului curent.
 * Returnează `null` pentru pagini unde provider-ul nu trebuie să intercepteze `?`
 * (ex: pagini de listing care au propriul handler `?` legacy).
 */
function determineContext(pathname: string): ShortcutContext | null {
  // Forms — invoices, proforme, avize, storno (toate folosesc invoice-form-v2.tsx)
  if (/^\/(facturi|proforme|avize|storno)\/[^/]+/.test(pathname)) {
    return 'form-invoice';
  }
  // NIR — receptie
  if (/^\/receptie\/[^/]+/.test(pathname)) {
    return 'form-nir';
  }
  // Pe restul paginilor, provider-ul nu intervine — listings au propriul handler.
  return null;
}

/**
 * Provider universal pentru modal-ul de scurtături tastatură.
 *
 * - Bind global pe `?` keydown
 * - Detect context din `usePathname` (Next.js)
 * - Active doar pe pagini de form (invoice/NIR)
 * - Nu se conflict cu listings — acelea au handler propriu legacy
 *
 * Mounted o dată, în dashboard layout.
 */
export function KeyboardShortcutsProvider() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname() || '';
  const context = determineContext(pathname);

  useEffect(() => {
    if (!context) return;
    const onKey = (e: KeyboardEvent) => {
      // Skip dacă focus-ul e într-un câmp editabil — user-ul tastează `?` în text
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      // Toggle pe `?` (cu Shift+/)
      if (e.key === '?') {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [context, open]);

  // Permite și deschiderea via custom event (din butonul ⌨ din footer-ul formularului)
  useEffect(() => {
    if (!context) return;
    const onOpen = () => setOpen(true);
    window.addEventListener('open-keyboard-shortcuts', onOpen);
    return () => window.removeEventListener('open-keyboard-shortcuts', onOpen);
  }, [context]);

  if (!context) return null;

  return (
    <KeyboardShortcutsModal
      open={open}
      onClose={() => setOpen(false)}
      context={context}
    />
  );
}
